import React, { useState } from 'react';

import { BsFiletypePdf } from 'react-icons/bs';
import image from '../../shared/assets/png/error404.png';
import {
  getProjectByID,
  getProjectAdditionalDocs,
  shareAdditionalDocuments,
  uploadProjectAdditionalDocs,
  uploadFilesToS3
} from '../services/api/implementation/impl';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { AiOutlineFileImage } from 'react-icons/ai';
import { FaFileAlt } from 'react-icons/fa';
import { FaRegFile } from 'react-icons/fa';
import { MdShare } from 'react-icons/md';
import { IoMdClose } from 'react-icons/io';
import { FiUpload } from 'react-icons/fi';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import Button from '../../shared/components/ui/Button';
import Modal from '../../shared/components/ui/Modal';
import toast from 'react-hot-toast';
import Loader from '../../shared/components/ui/Loader';

export const ProjectDocument = () => {
  const userId = Cookies.get('userId');
  const { id: projectId } = useParams();

  // Share modal state
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [emailBody, setEmailBody] = useState('');

  // Multi-selection state
  const [selectedForSharing, setSelectedForSharing] = useState([]);

  // Upload state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileNames, setFileNames] = useState({}); // Store custom names for each file
  const [isUploading, setIsUploading] = useState(false);

  /* 
      Data fetching
    */
  const { data: project, isLoading: isProjectLoading } = useQuery({
    queryKey: ['project'],
    queryFn: () => getProjectByID(projectId)
  });

  const queryClient = useQueryClient();

  const {
    data: projectAdditionalDocs,
    refetch: refetchAdditionalDocs,
    isFetching
  } = useQuery({
    queryKey: ['projectAdditionalDocs', projectId],
    queryFn: () => getProjectAdditionalDocs(projectId)
  });

  console.log({ project });

  const getFileIcon = (fileName) => {
    const extension = fileName?.split('.')?.pop()?.toLowerCase();

    switch (extension) {
      case 'pdf':
        return <BsFiletypePdf size={80} />;
      case 'png':
      case 'jpg':
      case 'jpeg':
        return <AiOutlineFileImage size={90} />;
      default:
        return <FaRegFile size={80} />; // Default icon for other file types
    }
  };

  const truncateFileName = (fileName, maxLength = 15) => {
    if (fileName.length <= maxLength) return fileName;
    const extension = fileName.split('.').pop();
    const nameWithoutExtension = fileName.slice(0, -(extension.length + 1));
    return `${nameWithoutExtension.slice(0, maxLength - 3)}...${extension}`;
  };

  // Multi-selection functions
  const toggleDocumentSelection = (document) => {
    setSelectedForSharing((prev) => {
      const isSelected = prev.some((doc) => doc.id === document.id);
      if (isSelected) {
        return prev.filter((doc) => doc.id !== document.id);
      } else {
        return [...prev, document];
      }
    });
  };

  const isDocumentSelected = (document) => {
    return selectedForSharing.some((doc) => doc.id === document.id);
  };

  const clearSelection = () => {
    setSelectedForSharing([]);
  };

  // Share functionality
  const handleShareClick = (document) => {
    setSelectedDocuments([document]);
    setEmailBody(`Please find the document below:\n\n${document.name}\n${document.url}`);
    setShowShareModal(true);
  };

  const handleBulkShare = () => {
    if (selectedForSharing.length === 0) {
      toast.error('Please select at least one document to share');
      return;
    }

    setSelectedDocuments(selectedForSharing);
    setEmailBody(
      `Please find the documents below:\n\n${selectedForSharing.map((doc) => `${doc.name}\n${doc.url}`).join('\n\n')}`
    );
    setShowShareModal(true);
  };

  const handleShareMultiple = (documents) => {
    setSelectedDocuments(documents);
    setEmailBody(
      `Please find the documents below:\n\n${documents.map((doc) => `${doc.name}\n${doc.url}`).join('\n\n')}`
    );
    setShowShareModal(true);
  };

  const { mutate: handleSendEmail, isLoading: isSendingEmail } = useMutation({
    mutationFn: async () => {
      // Validate required fields
      if (!emailRecipients.trim()) {
        throw new Error('Please enter email recipients');
      }

      // Parse email recipients (comma or space separated)
      const emails = emailRecipients
        .split(/[,\s]+/)
        .map((email) => email.trim())
        .filter((email) => email);

      // Extract document URLs
      const docs = selectedDocuments.map((doc) => doc.url);

      const payload = {
        emails,
        docs,
        body: emailBody || ''
      };

      return await shareAdditionalDocuments(payload);
    },
    onSuccess: () => {
      toast.success('Documents shared successfully!');
      setShowShareModal(false);

      // Reset form and clear selections
      setSelectedDocuments([]);
      setSelectedForSharing([]);
      setEmailRecipients('');
      setEmailBody('');
    },
    onError: (error) => {
      console.error('Error sharing documents:', error);
      toast.error(error?.message || error?.response?.data?.message || 'Failed to share documents');
    }
  });

  const handleCopyToClipboard = () => {
    try {
      // Copy only the email body
      navigator.clipboard
        .writeText(emailBody)
        .then(() => {
          toast.success('Email body copied to clipboard!');
        })
        .catch(() => {
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = emailBody;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          toast.success('Email body copied to clipboard!');
        });
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error('Error copying to clipboard. Please try again.');
    }
  };

  const closeShareModal = () => {
    setShowShareModal(false);
    setSelectedDocuments([]);
    setEmailRecipients('');
    setEmailBody('');
  };

  // Upload functionality
  const handleFileSelect = (event) => {
    const newFiles = Array.from(event.target.files);
    setSelectedFiles((prev) => [...prev, ...newFiles]);
    // Clear the input so the same files can be selected again if needed
    event.target.value = '';
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    // Also remove the custom name for this file
    setFileNames((prev) => {
      const newNames = { ...prev };
      delete newNames[index];
      return newNames;
    });
  };

  const handleFileNameChange = (index, customName) => {
    setFileNames((prev) => ({
      ...prev,
      [index]: customName
    }));
  };

  const openUploadModal = () => {
    setShowUploadModal(true);
    setSelectedFiles([]);
    setFileNames({});
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setSelectedFiles([]);
    setFileNames({});
  };

  const { mutate: handleUploadFiles } = useMutation({
    mutationFn: async ({ files, customNames }) => {
      // Step 1: Upload files to S3 first
      const uploadedFiles = await uploadFilesToS3(files);

      // Step 2: Create the payload with custom names and S3 URLs
      const docs = uploadedFiles.map((file, index) => {
        // Get the file extension from the original file
        const originalFileName = files[index].name;
        const fileExtension = originalFileName.substring(originalFileName.lastIndexOf('.'));

        // Append extension to custom name
        const nameWithExtension = customNames[index]
          ? `${customNames[index]}${fileExtension}`
          : file.name;

        return {
          name: nameWithExtension,
          url: file.url
        };
      });

      const payload = {
        project_id: parseInt(projectId),
        docs: docs
      };

      // Step 3: Send the docs with URLs to the project API
      return await uploadProjectAdditionalDocs(payload);
    },
    onSuccess: () => {
      toast.success('Files uploaded successfully!');
      setShowUploadModal(false);
      setSelectedFiles([]);
      setFileNames({});
      refetchAdditionalDocs();
      queryClient.invalidateQueries(['projectAdditionalDocs', projectId]);
      setIsUploading(false);
    },
    onError: (error) => {
      console.error('Upload error:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to upload files');
    }
  });

  const handleUploadSubmit = () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select at least one file to upload');
      return;
    }

    // Validate that all files have custom names
    const missingNames = selectedFiles.some((file, index) => !fileNames[index]?.trim());
    if (missingNames) {
      toast.error('Please provide a name for all documents');
      return;
    }

    setIsUploading(true);
    handleUploadFiles({ files: selectedFiles, customNames: fileNames });
  };

  let newArrayFiles = [];
  let projectImages = project?.projectImages?.map((item) => item);
  let buildingFiles = project?.buildings?.map((item) => [
    ...item.additionalDocuments,
    ...item.layoutPlans,
    ...item.developmentOrders,
    ...item.uploadLetterheads,
    ...item.googleMapImages
  ]);
  let googleImage = project?.googleImage?.map((item) => item);
  let brochures = project?.brochures?.map((item) => item);
  let tncp_approved_plan = project?.tncp_approved_plan?.map((item) => item);
  let tncp_sanctioned_layout_plan = project?.tncp_sanctioned_layout_plan?.map((item) => item);
  let projectSpecification = project?.projectSpecification.map((item) => item);

  console.log(buildingFiles, projectImages);

  return (
    <>
      <div className="w-full flex flex-col pl-5 pr-5 bg-gray-100 pb-10 overflow-hidden mt-5">
        <Loader isVisible={isProjectLoading || isUploading || isFetching} />
        {/* Bulk Share Controls */}
        {selectedForSharing.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-blue-800 font-medium">
                  {selectedForSharing.length} document{selectedForSharing.length > 1 ? 's' : ''}{' '}
                  selected
                </span>
                <button
                  onClick={clearSelection}
                  className="text-blue-600 hover:text-blue-800 text-sm underline">
                  Clear Selection
                </button>
              </div>
              <Button
                onClick={handleBulkShare}
                className="bg-blue-600 hover:bg-blue-700 text-white">
                Share Selected ({selectedForSharing.length})
              </Button>
            </div>
          </div>
        )}
        <div className="flex flex-col gap-10">
          <div className="flex flex-col gap-10">
            <h3 className="border w-[10rem] text-center font-semibold p-2 rounded-lg bg-slate-600 text-lg text-white">
              Project Images
            </h3>
            <div className="flex  gap-3">
              {projectImages?.map((item, i) => (
                <div
                  key={i}
                  className="relative flex flex-col border w-[12rem] p-5 rounded-xl justify-center items-center group">
                  {/* Checkbox for multi-selection */}
                  <input
                    type="checkbox"
                    checked={isDocumentSelected(item)}
                    onChange={() => toggleDocumentSelection(item)}
                    className="absolute top-2 left-2 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />

                  <a href={item.url} target="_blank" rel="noreferrer" download>
                    <AiOutlineFileImage size={80} />
                  </a>
                  <p className="text-center">{truncateFileName(item.name)}</p>

                  {/* Share Icon */}
                  <button
                    onClick={() => handleShareClick(item)}
                    className="absolute top-2 right-2 bg-blue-500 text-white p-1 rounded-full hover:bg-blue-600 transition-colors duration-200"
                    title="Share this document">
                    <MdShare size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-10 border-t pt-5 border-gray-300">
            <h3 className="border w-[10rem] text-center font-semibold p-2 rounded-lg bg-slate-600 text-lg text-white">
              Project files
            </h3>
            <div className="flex gap-3">
              {buildingFiles && buildingFiles.length > 0
                ? buildingFiles[0]?.map((file, i) => (
                    <div
                      key={i}
                      className="relative flex border w-[12rem] p-5 rounded-xl flex-col justify-center items-center group">
                      {/* Checkbox for multi-selection */}
                      <input
                        type="checkbox"
                        checked={isDocumentSelected(file)}
                        onChange={() => toggleDocumentSelection(file)}
                        className="absolute top-2 left-2 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      />

                      <a
                        href={file.url}
                        download={file.name}
                        target="_blank"
                        rel="noopener noreferrer">
                        {getFileIcon(file.name)}
                      </a>
                      <p className="text-center">{truncateFileName(file.name)}</p>

                      {/* Share Icon */}
                      <button
                        onClick={() => handleShareClick(file)}
                        className="absolute top-2 right-2 bg-blue-500 text-white p-1 rounded-full hover:bg-blue-600 transition-colors duration-200"
                        title="Share this document">
                        <MdShare size={16} />
                      </button>
                    </div>
                  ))
                : null}
              {googleImage?.map((item, i) => (
                <div
                  key={i}
                  className="relative flex flex-col border w-[12rem] p-5 rounded-xl justify-center items-center group">
                  {/* Checkbox for multi-selection */}
                  <input
                    type="checkbox"
                    checked={isDocumentSelected(item)}
                    onChange={() => toggleDocumentSelection(item)}
                    className="absolute top-2 left-2 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />

                  <a href={item.url} target="_blank" rel="noreferrer" download>
                    <AiOutlineFileImage size={80} />
                  </a>
                  <p className="text-center">{truncateFileName(item.name)}</p>

                  {/* Share Icon */}
                  <button
                    onClick={() => handleShareClick(item)}
                    className="absolute top-2 right-2 bg-blue-500 text-white p-1 rounded-full hover:bg-blue-600 transition-colors duration-200"
                    title="Share this document">
                    <MdShare size={16} />
                  </button>
                </div>
              ))}
              {brochures?.map((item, i) => (
                <div
                  key={i}
                  className="relative flex flex-col border w-[12rem] p-5 rounded-xl justify-center items-center group">
                  {/* Checkbox for multi-selection */}
                  <input
                    type="checkbox"
                    checked={isDocumentSelected(item)}
                    onChange={() => toggleDocumentSelection(item)}
                    className="absolute top-2 left-2 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />

                  <a href={item.url} target="_blank" rel="noreferrer" download>
                    <AiOutlineFileImage size={80} />
                  </a>
                  <p className="text-center">{truncateFileName(item.name)}</p>

                  {/* Share Icon */}
                  <button
                    onClick={() => handleShareClick(item)}
                    className="absolute top-2 right-2 bg-blue-500 text-white p-1 rounded-full hover:bg-blue-600 transition-colors duration-200"
                    title="Share this document">
                    <MdShare size={16} />
                  </button>
                </div>
              ))}
              {tncp_approved_plan?.map((item, i) => (
                <div
                  key={i}
                  className="relative flex flex-col border w-[12rem] p-5 rounded-xl justify-center items-center group">
                  {/* Checkbox for multi-selection */}
                  <input
                    type="checkbox"
                    checked={isDocumentSelected(item)}
                    onChange={() => toggleDocumentSelection(item)}
                    className="absolute top-2 left-2 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />

                  <a href={item.url} target="_blank" rel="noreferrer" download>
                    <AiOutlineFileImage size={80} />
                  </a>
                  <p className="text-center">{truncateFileName(item.name)}</p>

                  {/* Share Icon */}
                  <button
                    onClick={() => handleShareClick(item)}
                    className="absolute top-2 right-2 bg-blue-500 text-white p-1 rounded-full hover:bg-blue-600 transition-colors duration-200"
                    title="Share this document">
                    <MdShare size={16} />
                  </button>
                </div>
              ))}
              {tncp_sanctioned_layout_plan?.map((item, i) => (
                <div
                  key={i}
                  className="relative flex flex-col border w-[12rem] p-5 rounded-xl justify-center items-center group">
                  {/* Checkbox for multi-selection */}
                  <input
                    type="checkbox"
                    checked={isDocumentSelected(item)}
                    onChange={() => toggleDocumentSelection(item)}
                    className="absolute top-2 left-2 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />

                  <a href={item.url} target="_blank" rel="noreferrer" download>
                    <AiOutlineFileImage size={80} />
                  </a>
                  <p className="text-center">{truncateFileName(item.name)}</p>

                  {/* Share Icon */}
                  <button
                    onClick={() => handleShareClick(item)}
                    className="absolute top-2 right-2 bg-blue-500 text-white p-1 rounded-full hover:bg-blue-600 transition-colors duration-200"
                    title="Share this document">
                    <MdShare size={16} />
                  </button>
                </div>
              ))}
              {projectSpecification?.map((item, i) => (
                <div
                  key={i}
                  className="relative flex flex-col border w-[12rem] p-5 rounded-xl justify-center items-center group">
                  {/* Checkbox for multi-selection */}
                  <input
                    type="checkbox"
                    checked={isDocumentSelected(item)}
                    onChange={() => toggleDocumentSelection(item)}
                    className="absolute top-2 left-2 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />

                  <a href={item.url} target="_blank" rel="noreferrer" download>
                    <AiOutlineFileImage size={80} />
                  </a>
                  <p className="text-center">{truncateFileName(item.name)}</p>

                  {/* Share Icon */}
                  <button
                    onClick={() => handleShareClick(item)}
                    className="absolute top-2 right-2 bg-blue-500 text-white p-1 rounded-full hover:bg-blue-600 transition-colors duration-200"
                    title="Share this document">
                    <MdShare size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-10 border-t pt-5 border-gray-300">
            <div className="flex items-center justify-between">
              <h3 className="border text-center font-semibold p-2 rounded-lg bg-slate-600 text-lg text-white whitespace-nowrap">
                Additional Documents
              </h3>
              <button
                onClick={openUploadModal}
                type="button"
                className={`border border-customBlue bg-customBlue text-white  py-2 px-4 rounded-lg focus:outline-none focus:shadow-outline flex items-center gap-2`}>
                <FiUpload size={16} />
                Upload Files
              </button>
            </div>
            <div className="flex gap-3 flex-wrap">
              {projectAdditionalDocs?.map((item, i) => (
                <div
                  key={i}
                  className="relative flex flex-col border w-[12rem] p-5 rounded-xl justify-center items-center group">
                  {/* Checkbox for multi-selection */}
                  <input
                    type="checkbox"
                    checked={isDocumentSelected(item)}
                    onChange={() => toggleDocumentSelection(item)}
                    className="absolute top-2 left-2 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  />

                  <a href={item.url} target="_blank" rel="noreferrer" download>
                    {getFileIcon(item.name)}
                  </a>
                  <p className="text-center">{truncateFileName(item.name)}</p>

                  {/* Share Icon */}
                  <button
                    onClick={() => handleShareClick(item)}
                    className="absolute top-2 right-2 bg-blue-500 text-white p-1 rounded-full hover:bg-blue-600 transition-colors duration-200"
                    title="Share this document">
                    <MdShare size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <Modal isDirty confirmOnClose width={50} name="Share Document" onClose={closeShareModal}>
          <div className="flex flex-col gap-4">
            {/* Selected Documents Preview */}
            {selectedDocuments.length > 0 && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Selected {selectedDocuments.length === 1 ? 'document' : 'documents'}:
                </p>
                <div className="space-y-1">
                  {selectedDocuments.map((doc, index) => (
                    <p key={index} className="text-sm text-gray-600">
                      • {doc.name}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {/* Email Form */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Recipients *
              </label>
              <input
                type="email"
                placeholder="Enter email addresses (comma-separated)"
                value={emailRecipients}
                onChange={(e) => setEmailRecipients(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Body</label>
              <textarea
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={6}
                placeholder="Email body content..."
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" onClick={closeShareModal} displayType="secondary">
                Cancel
              </Button>
              <Button type="button" onClick={handleCopyToClipboard} displayType="secondary">
                Copy to Clipboard
              </Button>
              <Button
                type="button"
                onClick={handleSendEmail}
                disabled={!emailRecipients || isSendingEmail}>
                {isSendingEmail ? 'Sending...' : 'Send Email'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <Modal
          isDirty
          confirmOnClose
          width={50}
          name="Upload Additional Documents"
          onClose={closeUploadModal}>
          <div className="flex flex-col gap-4 relative max-h-[48rem]">
            {/* File Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Files (Multiple files allowed)
              </label>
              <input
                type="file"
                multiple
                onChange={handleFileSelect}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.txt,.xlsx,.xls"
              />
              <p className="text-xs text-gray-500 mt-1">
                Supported formats: PDF, DOC, DOCX, JPG, JPEG, PNG, GIF, TXT, XLSX, XLS
              </p>
            </div>

            {/* Selected Files Preview */}
            {selectedFiles.length > 0 && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-sm font-medium text-gray-700">
                    Selected files ({selectedFiles.length}):
                  </p>
                  <button
                    onClick={() => setSelectedFiles([])}
                    className="text-red-600 hover:text-red-800 text-sm underline">
                    Clear All
                  </button>
                </div>
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {selectedFiles.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 bg-white p-3 rounded border">
                      <div className="flex-shrink-0">{getFileIcon(file.name)}</div>
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 mb-1">
                          Original file:{' '}
                          <span className="font-medium text-gray-700">{file.name}</span>
                        </p>
                        <input
                          type="text"
                          placeholder="Enter custom document name *"
                          value={fileNames[index] || ''}
                          onChange={(e) => handleFileNameChange(index, e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none"
                          required
                        />
                      </div>
                      <button
                        onClick={() => removeSelectedFile(index)}
                        className="text-red-500 hover:text-red-700 flex-shrink-0">
                        <IoMdClose size={20} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" onClick={closeUploadModal} displayType="secondary">
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleUploadSubmit}
                disabled={selectedFiles.length === 0 || isUploading}
                className="bg-green-600 hover:bg-green-700 text-white">
                {isUploading
                  ? 'Uploading...'
                  : `Upload ${selectedFiles.length} File${selectedFiles.length !== 1 ? 's' : ''}`}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};
